/**
 * Deadline rows of `/settings/delivery-flow` (#2454): the packing cutoff, the
 * dispatch cutoff and the delivery window, each with the category the flow
 * gives it (CONTEXT.md 「截止时间」) and the source of its value. The dispatch
 * cutoff also carries the intake rule, which is not a flow category (#2636).
 *
 * Editing follows `TenantSettingsCard` (#2429): the Pencil opens inputs in
 * the value cell, one row at a time, with "Save", "Cancel" and, for a tenant
 * value, "Reset to default" (`null`). An input is prefilled only with a TENANT
 * value; an emptied input saves `null`.
 *
 * The delivery window is one row with two inputs, always sent as a pair
 * (#2449 refuses a lone half). Both empty saves the pair as `null`; one empty
 * half, or a start not before the end (`HH:mm` compares as a string), is
 * refused here with an error and no request.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Input, Label } from '@resource-kit/ui/react';
import type { TenantSettings, TenantSettingsBody } from '@/services/tenants';
import { requestErrorMessage, useSaveTenantSettings } from '../hooks/useTenants';
import { EditButton, SettingsRow } from './SettingsSection';
import {
  type DeliveryFlow,
  EM_DASH,
  type FALLBACK_LABEL_KEY,
  SourceBadge,
  settingsBody,
  tenantValue,
} from './deliveryFlowShared';

type GateKind = DeliveryFlow['gates'][number]['kind'];
type GateCategory = DeliveryFlow['gates'][number]['category'];

const CATEGORY_KEY = {
  mandatory: 'deliveryFlow.gateClass.mandatory',
  reminder: 'deliveryFlow.gateClass.hint',
  commitment: 'deliveryFlow.gateClass.promise',
} as const satisfies Record<GateCategory, string>;

type SingleField = 'packagingCutoffTime' | 'dispatchCutoffTime';
type GateRow = SingleField | 'deliveryWindow';

const SINGLE_ROWS = [
  {
    field: 'packagingCutoffTime',
    gate: 'packaging_cutoff',
    labelKey: 'deliveryFlow.gate.packaging_cutoff',
    fallback: 'default',
  },
  {
    field: 'dispatchCutoffTime',
    gate: 'dispatch_cutoff',
    labelKey: 'deliveryFlow.gate.dispatch_cutoff',
    intakeRule: true,
    noteKey: 'deliveryFlow.gate.dispatch_cutoff.note',
    fallback: 'default',
  },
] as const satisfies readonly {
  field: SingleField;
  gate: GateKind;
  labelKey: string;
  intakeRule?: true;
  noteKey?: string;
  fallback: keyof typeof FALLBACK_LABEL_KEY;
}[];

function gateTime(flow: DeliveryFlow, kind: GateKind): string {
  return flow.gates.find((gate) => gate.kind === kind)?.local_time ?? EM_DASH;
}

export function DeliveryFlowGateRows({
  flow,
  settings,
}: {
  flow: DeliveryFlow;
  settings: TenantSettings;
}) {
  const { t } = useTranslation();
  const mutation = useSaveTenantSettings();
  const [editing, setEditing] = useState<GateRow | null>(null);
  const [draft, setDraft] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  const [invalid, setInvalid] = useState(false);

  const open = (row: GateRow) => {
    setEditing(row);
    setInvalid(false);
    mutation.reset();
    if (row === 'deliveryWindow') {
      setDraft(tenantValue(settings.deliveryWindowStart) ?? '');
      setDraftEnd(tenantValue(settings.deliveryWindowEnd) ?? '');
    } else {
      setDraft(tenantValue(settings[row]) ?? '');
    }
  };
  const close = () => {
    setEditing(null);
    setDraft('');
    setDraftEnd('');
    setInvalid(false);
  };
  const cancel = () => {
    close();
    mutation.reset();
  };
  const save = (edit: Partial<TenantSettingsBody>) =>
    mutation.mutate(settingsBody(settings, edit), { onSuccess: close });
  const saveWindow = () => {
    const start = draft.trim();
    const end = draftEnd.trim();
    if (start === '' && end === '') {
      save({ deliveryWindowStart: null, deliveryWindowEnd: null });
      return;
    }
    if (start === '' || end === '' || start >= end) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    save({ deliveryWindowStart: start, deliveryWindowEnd: end });
  };

  const editActions = (isTenant: boolean, onSave: () => void, onReset: () => void) => (
    <>
      {isTenant && (
        <Button variant="ghost" size="sm" disabled={mutation.isPending} onClick={onReset}>
          {t('tenants.settings.resetToDefault')}
        </Button>
      )}
      <Button variant="outline" size="sm" disabled={mutation.isPending} onClick={cancel}>
        {t('tenants.settings.cancel')}
      </Button>
      <Button size="sm" disabled={mutation.isPending} onClick={onSave}>
        {mutation.isPending ? t('tenants.settings.saving') : t('tenants.settings.save')}
      </Button>
    </>
  );
  const saveError = mutation.isError && (
    <p id="delivery-flow-save-error" role="alert" className="text-sm text-destructive">
      {t('tenants.settings.saveFailed', { message: requestErrorMessage(mutation.error) })}
    </p>
  );
  const categoryBadge = (kind: GateKind, intakeRule = false) => {
    const category = flow.gates.find((gate) => gate.kind === kind)?.category;
    if (!category) return null;
    const label = t(CATEGORY_KEY[category]);
    return (
      <Badge variant="outline">
        {intakeRule ? t('deliveryFlow.gateClass.dispatch', { category: label }) : label}
      </Badge>
    );
  };

  const errorId = invalid
    ? 'delivery-flow-window-error'
    : mutation.isError
      ? 'delivery-flow-save-error'
      : undefined;
  const windowLabel = t('deliveryFlow.gate.delivery_window');
  const windowSource = settings.deliveryWindowEnd.source;
  const isEditingWindow = editing === 'deliveryWindow';

  return (
    <>
      {SINGLE_ROWS.map((row) => {
        const { field, gate, labelKey, fallback } = row;
        const noteKey = 'noteKey' in row ? row.noteKey : undefined;
        const setting = settings[field];
        const id = `delivery-flow-${field}`;
        const label = t(labelKey);
        const isEditing = editing === field;
        return (
          <SettingsRow
            key={field}
            testId={`delivery-flow-row-${field}`}
            label={label}
            labelFor={isEditing ? id : undefined}
            action={
              isEditing ? (
                editActions(
                  setting.source === 'tenant',
                  () => save({ [field]: draft.trim() || null }),
                  () => save({ [field]: null }),
                )
              ) : (
                <EditButton
                  label={t('tenants.settings.edit', { label })}
                  onClick={() => open(field)}
                />
              )
            }
          >
            {isEditing ? (
              <div className="space-y-2">
                <Input
                  id={id}
                  type="time"
                  className="h-9 max-w-48"
                  aria-invalid={mutation.isError || undefined}
                  aria-describedby={mutation.isError ? 'delivery-flow-save-error' : undefined}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
                {saveError}
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm">{gateTime(flow, gate)}</span>
                  {categoryBadge(gate, 'intakeRule' in row)}
                  <SourceBadge source={setting.source} fallback={fallback} />
                </div>
                {noteKey && <p className="text-xs text-muted-foreground">{t(noteKey)}</p>}
              </div>
            )}
          </SettingsRow>
        );
      })}
      <SettingsRow
        testId="delivery-flow-row-deliveryWindow"
        label={windowLabel}
        action={
          isEditingWindow ? (
            editActions(windowSource === 'tenant', saveWindow, () =>
              save({ deliveryWindowStart: null, deliveryWindowEnd: null }),
            )
          ) : (
            <EditButton
              label={t('tenants.settings.edit', { label: windowLabel })}
              onClick={() => open('deliveryWindow')}
            />
          )
        }
      >
        {isEditingWindow ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label htmlFor="delivery-flow-window-start" className="font-normal">
                  {t('deliveryFlow.window.start')}
                </Label>
                <Input
                  id="delivery-flow-window-start"
                  type="time"
                  className="h-9 w-36"
                  aria-invalid={invalid || mutation.isError || undefined}
                  aria-describedby={errorId}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="delivery-flow-window-end" className="font-normal">
                  {t('deliveryFlow.window.end')}
                </Label>
                <Input
                  id="delivery-flow-window-end"
                  type="time"
                  className="h-9 w-36"
                  aria-invalid={invalid || mutation.isError || undefined}
                  aria-describedby={errorId}
                  value={draftEnd}
                  onChange={(e) => setDraftEnd(e.target.value)}
                />
              </div>
            </div>
            {invalid && (
              <p id="delivery-flow-window-error" role="alert" className="text-sm text-destructive">
                {t('deliveryFlow.window.invalid')}
              </p>
            )}
            {saveError}
          </div>
        ) : (
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground">{t('deliveryFlow.window.start')}</span>
              <span className="font-mono">{flow.delivery_window_start}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground">{t('deliveryFlow.window.end')}</span>
              <span className="font-mono">{gateTime(flow, 'delivery_window_end')}</span>
              {categoryBadge('delivery_window_end')}
              <SourceBadge source={windowSource} fallback="default" />
            </div>
          </div>
        )}
      </SettingsRow>
      {editing === null && mutation.isSuccess && (
        <p className="px-5 py-2 text-sm text-success">{t('tenants.settings.saved')}</p>
      )}
    </>
  );
}
